import React, { useCallback, useEffect, useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  KEY_DOWN_COMMAND,
  LexicalCommand,
} from "lexical"

import { $createPlaceholderNode } from "@/components/lexical/nodes/placeholder/node"
import { PlaceholderData, registerPlaceholders, SEQUENCE_EMAIL_PLACEHOLDERS } from "@/components/lexical/placeholders"

import { PlaceholderCombobox } from "./placeholder-combobox"

export const OPEN_PLACEHOLDER_PICKER_COMMAND: LexicalCommand<{
  anchorRect?: DOMRect
  placement?: "top" | "bottom"
}> = createCommand("OPEN_PLACEHOLDER_PICKER_COMMAND")

export const INSERT_PLACEHOLDER_COMMAND: LexicalCommand<PlaceholderData> = createCommand("INSERT_PLACEHOLDER_COMMAND")

interface PlaceholderPluginProps {
  placeholders?: PlaceholderData[]
}

function getCaretRect(): DOMRect | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0)
  const rect = range.getBoundingClientRect()

  if (rect.width === 0 && rect.height === 0) {
    const span = document.createElement("span")
    span.textContent = "\u200B"
    range.insertNode(span)
    const spanRect = span.getBoundingClientRect()
    span.parentNode?.removeChild(span)
    selection.removeAllRanges()
    selection.addRange(range)
    return spanRect
  }

  return rect
}

export default function PlaceholderPlugin({
  placeholders = SEQUENCE_EMAIL_PLACEHOLDERS,
}: PlaceholderPluginProps): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [isOpen, setIsOpen] = useState(false)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const [triggeredByBrace, setTriggeredByBrace] = useState(false)
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom")

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

  registerPlaceholders(placeholders)

  const openPicker = useCallback(
    (rect: DOMRect | null, newPlacement: "top" | "bottom" = "bottom") => {
      const rootElement = editor.getRootElement()
      if (rootElement) {
        const dialogContent = rootElement.closest('[role="dialog"], [role="alertdialog"]')
        setPortalContainer(dialogContent as HTMLElement | null)
      }
      editor.blur()
      setAnchorRect(rect)
      setPlacement(newPlacement)
      setIsOpen(true)
    },
    [editor]
  )

  const closePicker = useCallback(() => {
    setIsOpen(false)
    setAnchorRect(null)
    setTriggeredByBrace(false)
    editor.focus()
  }, [editor])

  const handleSelect = useCallback(
    (placeholder: PlaceholderData) => {
      editor.update(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return

        if (triggeredByBrace) {
          const anchorNode = selection.anchor.getNode()
          const anchorOffset = selection.anchor.offset

          if (anchorOffset > 0) {
            const textContent = anchorNode.getTextContent()
            const charBefore = textContent[anchorOffset - 1]

            if (charBefore === "{") {
              selection.anchor.set(selection.anchor.key, anchorOffset - 1, "text")
              selection.focus.set(selection.focus.key, anchorOffset, "text")
              selection.removeText()
            }
          }
        }

        const placeholderNode = $createPlaceholderNode(
          placeholder.label,
          placeholder.value,
          placeholder.id,
          placeholder.description
        )
        selection.insertNodes([placeholderNode])

        const spaceNode = $createTextNode(" ")
        placeholderNode.insertAfter(spaceNode)
        spaceNode.select()
      })

      closePicker()
    },
    [editor, triggeredByBrace, closePicker]
  )

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.key === "{" && !event.ctrlKey && !event.metaKey && !event.altKey) {
          requestAnimationFrame(() => {
            const rect = getCaretRect()
            if (rect) {
              setTriggeredByBrace(true)
              openPicker(rect)
            }
          })
        }
        return false
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor, openPicker])

  useEffect(() => {
    return editor.registerCommand(
      OPEN_PLACEHOLDER_PICKER_COMMAND,
      (payload) => {
        const rect = payload?.anchorRect || getCaretRect()
        if (rect) {
          setTriggeredByBrace(false)
          openPicker(rect, payload?.placement)
        }
        return true
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor, openPicker])

  useEffect(() => {
    return editor.registerCommand(
      INSERT_PLACEHOLDER_COMMAND,
      (placeholder) => {
        editor.update(() => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return

          const placeholderNode = $createPlaceholderNode(
            placeholder.label,
            placeholder.value,
            placeholder.id,
            placeholder.description
          )
          selection.insertNodes([placeholderNode])

          const spaceNode = $createTextNode(" ")
          placeholderNode.insertAfter(spaceNode)
          spaceNode.select()
        })
        return true
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor])

  return (
    <PlaceholderCombobox
      placeholders={placeholders}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closePicker()
      }}
      onSelect={handleSelect}
      anchorRect={anchorRect}
      placement={placement}
      portalContainer={portalContainer}
    />
  )
}

export { PlaceholderCombobox } from "./placeholder-combobox"
export { PlaceholderPickerPopover } from "./placeholder-picker-popover"
